import { Box, Container, Paper, Typography } from '@mui/material';
import { useCookies } from 'react-cookie';
import Footer from './Footer';
import LawAppBar from './LawAppBar';
import './Privacy.css';

export default function Privacy() {
    const [cookies] = useCookies(['given_name', 'userid', 'picture', 'email', 'credential']);

    return (
        <div className="App">
            <LawAppBar cookies={cookies} />
            <Container
                maxWidth="lg"
                sx={{
                    padding: { xs: '20px', sm: '40px' },
                    marginTop: '40px',
                    marginBottom: '60px',
                    textAlign: 'left'
                }}
            >
                {/* Header Section */}
                <Box sx={{ textAlign: 'center', marginBottom: '60px' }}>
                    <Typography
                        variant="h3"
                        component="h1"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 400,
                            fontSize: { xs: '2rem', sm: '2.8rem', md: '3.2rem' },
                            marginBottom: '1rem',
                            letterSpacing: '-0.02em',
                            lineHeight: 1.2
                        }}
                    >
                        Privacy Policy
                    </Typography>
                    <Typography
                        variant="h6"
                        sx={{
                            color: 'var(--text-secondary)',
                            fontSize: { xs: '1rem', sm: '1.2rem' },
                            fontWeight: 400,
                            fontStyle: 'italic'
                        }}
                    >
                        Last Updated: February 4, 2026
                    </Typography>
                </Box>

                {/* Introduction */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="h5"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.5rem', sm: '1.8rem' },
                            marginBottom: '20px'
                        }}
                    >
                        Your Privacy Is Important to Us
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '20px'
                        }}
                    >
                        This Privacy Policy explains how Veridical Technologies LLC, doing business as AttorneyAide ("AttorneyAide," "we," "our," or "us"), collects, uses, processes, and shares information when you visit our website or use our services (the "Services").
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '20px'
                        }}
                    >
                        If you have questions regarding this Privacy Policy or our data-handling practices, please contact us using the information provided at the end of this document.
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8
                        }}
                    >
                        AttorneyAide is built for legal professionals in the United States. We recognize that the information processed through our platform may include sensitive client data, attorney work product, and protected health information ("PHI"). Our privacy and security practices are designed to support your professional, ethical, and legal obligations.
                    </Typography>
                </Paper>

                {/* Section 1: Our Privacy-First Commitment */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '24px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        1. Our Privacy-First Commitment
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '20px'
                        }}
                    >
                        AttorneyAide is designed exclusively for use by licensed attorneys, law firms, and their authorized staff, and is not designed for direct use by consumers or clients. Our approach reflects the heightened confidentiality and accuracy standards required in legal practice.
                    </Typography>
                    <Typography
                        variant="h5"
                        component="h3"
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.3rem', sm: '1.5rem' },
                            marginTop: '24px',
                            marginBottom: '16px'
                        }}
                    >
                        No Model Training
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '20px'
                        }}
                    >
                        AttorneyAide does not use Customer Data to train, fine-tune, or improve general-purpose artificial intelligence models. Customer Data is processed solely to provide the Services or as otherwise expressly authorized by you or required by law. This commitment does not prohibit limited, human review of Customer Data solely for debugging, security, or support purposes at the customer's request or as necessary to maintain the Services, subject to strict confidentiality obligations.
                    </Typography>
                    <Typography
                        variant="h5"
                        component="h3"
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.3rem', sm: '1.5rem' },
                            marginTop: '24px',
                            marginBottom: '16px'
                        }}
                    >
                        Privilege-Aware Design
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '20px'
                        }}
                    >
                        Our platform is designed to support the preservation of attorney-client privilege and the work-product doctrine. AttorneyAide does not claim ownership of Customer Data and does not access or use such data except as necessary to provide the Services, to maintain platform security, or as required by law.
                    </Typography>
                    <Typography
                        variant="h5"
                        component="h3"
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.3rem', sm: '1.5rem' },
                            marginTop: '24px',
                            marginBottom: '16px'
                        }}
                    >
                        Data Minimization
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8
                        }}
                    >
                        We collect and process only the information reasonably necessary to operate and improve the Services. We do not sell, rent, or otherwise monetize Customer Data.
                    </Typography>
                </Paper>

                {/* Section 2: Information We Collect */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '24px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        2. Information We Collect
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '20px'
                        }}
                    >
                        We collect information in the following categories:
                    </Typography>
                    <Typography
                        variant="h5"
                        component="h3"
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.3rem', sm: '1.5rem' },
                            marginTop: '24px',
                            marginBottom: '16px'
                        }}
                    >
                        A. Information You Provide Directly
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '12px'
                        }}
                    >
                        This may include:
                    </Typography>
                    <Box component="ul" sx={{ paddingLeft: { xs: '20px', sm: '24px' }, margin: 0, textAlign: 'left', marginBottom: '20px' }}>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '12px'
                            }}
                        >
                            Documents and files uploaded to the Services
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '12px'
                            }}
                        >
                            Information entered into forms or workflows
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '12px'
                            }}
                        >
                            Account registration details (such as name, email address, userid, and profile photo)
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8
                            }}
                        >
                            Communications with us, including support requests
                        </Typography>
                    </Box>
                    <Typography
                        variant="h5"
                        component="h3"
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.3rem', sm: '1.5rem' },
                            marginTop: '24px',
                            marginBottom: '16px'
                        }}
                    >
                        B. Information Collected Automatically
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '12px'
                        }}
                    >
                        This may include:
                    </Typography>
                    <Box component="ul" sx={{ paddingLeft: { xs: '20px', sm: '24px' }, margin: 0, textAlign: 'left', marginBottom: '20px' }}>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '12px'
                            }}
                        >
                            Device and technical information (e.g., browser type, operating system, IP address, time zone, and usage logs)
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '12px'
                            }}
                        >
                            Website interaction data (such as pages viewed and features used)
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '12px'
                            }}
                        >
                            Workflow execution and system performance data for quality assurance and platform reliability
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8
                            }}
                        >
                            Cookies and similar technologies
                        </Typography>
                    </Box>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '20px',
                            fontStyle: 'italic'
                        }}
                    >
                        AttorneyAide does not store plaintext passwords.
                    </Typography>
                    <Typography
                        variant="h5"
                        component="h3"
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.3rem', sm: '1.5rem' },
                            marginTop: '24px',
                            marginBottom: '16px'
                        }}
                    >
                        C. Information from Third-Party or Public Sources
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '20px'
                        }}
                    >
                        We may receive limited information from publicly available sources or service providers strictly to support account creation, authentication, or service delivery.
                    </Typography>
                    <Typography
                        variant="h5"
                        component="h3"
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.3rem', sm: '1.5rem' },
                            marginTop: '24px',
                            marginBottom: '16px'
                        }}
                    >
                        D. Generated or Inferred Information
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '20px'
                        }}
                    >
                        Outputs generated from documents you upload (such as summaries, chronologies, or extracted data) are treated as Customer Data and are subject to this Privacy Policy.
                    </Typography>
                    <Typography
                        variant="h5"
                        component="h3"
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.3rem', sm: '1.5rem' },
                            marginTop: '24px',
                            marginBottom: '16px'
                        }}
                    >
                        E. Customer Data vs. Business Relationship Data
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8
                        }}
                    >
                        <b>Customer Data</b> includes case-related data, including case documents uploaded, case-related information entered, and case-related outputs generated through the Services, and is subject to this Privacy Policy.<br />
                        <b>Business Relationship Data</b> includes account registration details, device and technical information, and website interaction and performance data, which are used to operate, secure, and market the Services.
                    </Typography>
                </Paper>

                {/* Section 3: How We Use Information */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '24px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        3. How We Use Information
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '20px'
                        }}
                    >
                        We use information to:
                    </Typography>
                    <Box component="ul" sx={{ paddingLeft: { xs: '20px', sm: '24px' }, margin: 0, textAlign: 'left', marginBottom: '20px' }}>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '12px'
                            }}
                        >
                            Provide, operate, and maintain the Services
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '12px'
                            }}
                        >
                            Process documents and generate outputs at your direction
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '12px'
                            }}
                        >
                            Communicate with you regarding your account or service updates
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '12px'
                            }}
                        >
                            Provide customer support and respond to inquiries
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '12px'
                            }}
                        >
                            Improve system reliability, security, and performance
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '12px'
                            }}
                        >
                            Detect, prevent, and address fraud or misuse
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8
                            }}
                        >
                            Comply with applicable legal obligations
                        </Typography>
                    </Box>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8
                        }}
                    >
                        AttorneyAide does not use Customer Data for advertising targeting, behavioral profiling, or AI model training.
                    </Typography>
                </Paper>

                {/* Section 4: How We Share Information */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '24px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        4. How We Share Information
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '20px'
                        }}
                    >
                        AttorneyAide shares information only as described below.
                    </Typography>
                    <Typography
                        variant="h5"
                        component="h3"
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.3rem', sm: '1.5rem' },
                            marginTop: '24px',
                            marginBottom: '16px'
                        }}
                    >
                        A. With Your Authorization
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '20px'
                        }}
                    >
                        We may share information when you request or explicitly authorize us to do so.
                    </Typography>
                    <Typography
                        variant="h5"
                        component="h3"
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.3rem', sm: '1.5rem' },
                            marginTop: '24px',
                            marginBottom: '16px'
                        }}
                    >
                        B. Service Providers
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '20px'
                        }}
                    >
                        We may share information with trusted vendors and service providers who perform services on our behalf (such as cloud hosting, authentication, or analytics). These providers are contractually bound to confidentiality and data-protection obligations consistent with this Privacy Policy.
                    </Typography>
                    <Typography
                        variant="h5"
                        component="h3"
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.3rem', sm: '1.5rem' },
                            marginTop: '24px',
                            marginBottom: '16px'
                        }}
                    >
                        C. Legal and Safety Obligations
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '20px'
                        }}
                    >
                        We may disclose information if required to comply with applicable law, legal process, or governmental requests, or to protect the rights, property, or safety of AttorneyAide, our users, or others. Where legally permitted, we will provide notice to the customer before responding to a request for Customer Data.
                    </Typography>
                    <Typography
                        variant="h5"
                        component="h3"
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.3rem', sm: '1.5rem' },
                            marginTop: '24px',
                            marginBottom: '16px'
                        }}
                    >
                        D. Business Transactions
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '20px'
                        }}
                    >
                        If AttorneyAide is involved in a merger, acquisition, reorganization, or similar transaction, information may be transferred as part of that process, subject to continued protection consistent with this Privacy Policy.
                    </Typography>
                    <Typography
                        variant="h5"
                        component="h3"
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.3rem', sm: '1.5rem' },
                            marginTop: '24px',
                            marginBottom: '16px'
                        }}
                    >
                        E. Aggregated or De-Identified Data
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '20px'
                        }}
                    >
                        We may use or share aggregated or de-identified information that cannot reasonably be used to identify you or your clients.
                    </Typography>
                    <Typography
                        variant="h5"
                        component="h3"
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.3rem', sm: '1.5rem' },
                            marginTop: '24px',
                            marginBottom: '16px'
                        }}
                    >
                        F. Website Advertising and Analytics
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8
                        }}
                    >
                        AttorneyAide does not use or share Customer Data for advertising purposes. Website analytics and marketing activities, if any, are limited to Business Relationship Data and do not include case content, client information, or inferences drawn from case characteristics.                    </Typography>
                </Paper>

                {/* Section 5: Security Safeguards */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '24px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        5. Security Safeguards
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '20px'
                        }}
                    >
                        AttorneyAide employs administrative, technical, and physical safeguards designed to protect information against unauthorized access, disclosure, or misuse.
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '20px'
                        }}
                    >
                        Examples of security measures may include, but are not limited to:
                    </Typography>
                    <Box component="ul" sx={{ paddingLeft: { xs: '20px', sm: '24px' }, margin: 0, textAlign: 'left', marginBottom: '20px' }}>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '12px'
                            }}
                        >
                            End-to-end encryption for data in transit (TLS 1.3+)
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '12px'
                            }}
                        >
                            Encryption at rest using industry-standard AES-256
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '12px'
                            }}
                        >
                            OAuth 2.0 authentication with support for multi-factor authentication (MFA). AttorneyAide strongly recommends that all users enable multi-factor authentication. AttorneyAide relies on third-party enterprise authentication providers (e.g., Google Auth Platform) for user authentication.
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8
                            }}
                        >
                            Role-based access controls, access logging, and regular audits.
                        </Typography>
                    </Box>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '20px'
                        }}
                    >
                        Security practices are periodically reviewed and updated in light of evolving risks.
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8
                        }}
                    >
                        While we take reasonable measures to protect information, no system can be guaranteed to be completely secure.
                    </Typography>
                </Paper>

                {/* Section 6: HIPAA, State Health Privacy Laws, and Protected Health Information */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '24px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        6. HIPAA, State Health Privacy Laws, and Protected Health Information
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '20px'
                        }}
                    >
                        To the extent AttorneyAide acts as a Business Associate under HIPAA, we comply with applicable HIPAA requirements and execute Business Associate Agreements.
                    </Typography>
                    <Typography
                        variant="h5"
                        component="h3"
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.3rem', sm: '1.5rem' },
                            marginTop: '24px',
                            marginBottom: '16px'
                        }}
                    >
                        6 A. Washington State Residents – My Health My Data Act
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '20px'
                        }}
                    >
                        To the extent AttorneyAide collects, processes, or stores "consumer health data" as defined under the Washington My Health My Data Act (RCW 19.373), we do so solely for the purpose of providing the Services at your direction. AttorneyAide does not sell consumer health data and does not use such data for targeted advertising.
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8
                        }}
                    >
                        AttorneyAide processes consumer health data only as a service provider acting on behalf of its customers and in accordance with applicable legal and contractual obligations.
                    </Typography>
                </Paper>

                {/* Section 7: Data Retention and Deletion */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '24px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        7. Data Retention and Deletion
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '20px'
                        }}
                    >
                        We retain information only for as long as reasonably necessary to provide the Services, comply with legal obligations, resolve disputes, and enforce agreements.
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8
                        }}
                    >
                        Upon termination of your account, you may request deletion of Customer Data, subject to applicable legal, regulatory, or contractual retention requirements.
                    </Typography>
                </Paper>

                {/* Section 8: Third-Party Infrastructure */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '24px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        8. Third-Party Infrastructure
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '20px'
                        }}
                    >
                        AttorneyAide uses enterprise-grade cloud infrastructure providers (such as AWS or Google Cloud) that maintain recognized security certifications (e.g., SOC 2, ISO 27001). Where applicable, subprocessors are contractually required to meet equivalent data-protection obligations.
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8
                        }}
                    >
                        When third-party AI services are used, AttorneyAide utilizes private or restricted APIs configured not to retain or train on Customer Data.
                    </Typography>
                </Paper>

                {/* Section 9: Children's Information */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '24px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        9. Children's Information
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8
                        }}
                    >
                        The Services are intended solely for use by legal professionals and are not directed to children under 18.
                    </Typography>
                </Paper>

                {/* Section 10: Governing Law */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '24px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        10. Governing Law
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8
                        }}
                    >
                        This Privacy Policy is governed by the laws of the State of Washington, without regard to conflict of laws principles.
                    </Typography>
                </Paper>

                {/* Section 11: Changes to This Privacy Policy */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '24px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        11. Changes to This Privacy Policy
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8
                        }}
                    >
                        We may modify this Privacy Policy from time to time in which case we will update the "Last Updated" date at the top of these Terms. If we make changes that are material, we will use reasonable efforts to attempt to notify you, such as by e-mail and/or by placing a prominent notice on the first page of the Website. However, it is your sole responsibility to review this Privacy Policy from time to time to view any such changes. The updated Terms will be effective as of the time of posting, or such later date as may be specified in the updated Terms. Your continued access or use of the Services after the modifications have become effective will be deemed your acceptance of the modified Privacy Policy.
                    </Typography>
                </Paper>

                {/* Section 12: Contact Us */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '24px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        12. Contact Us
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8
                        }}
                    >
                        If you have questions about this Privacy Policy, our security practices, or require a Business Associate Agreement, please contact:
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginTop: '12px'
                        }}
                    >
                        Email: support@attorneyaide.com
                    </Typography>
                </Paper>
            </Container>
            <Footer />
        </div>
    );
}

